import dayjs from 'dayjs';
import type {DcaCondition, DcaConditions} from '../../../types/dca';

/** K线周期选项（与后端 DcaPlanService.TIMEFRAMES 一致） */
export const DCA_TIMEFRAMES = ['1m', '3m', '5m', '15m', '30m', '1H', '2H', '4H', '6H', '12H', '1D'];

/** 8 类条件类型（与后端 DcaConditions TYPE_* 一致） */
export const CONDITION_TYPES = [
    'EMA_CROSS',
    'PRICE_CROSS_EMA',
    'SMA_CROSS',
    'RSI',
    'MACD_CROSS',
    'PRICE_BELOW',
    'DRAWDOWN',
    'LOW_TOUCH',
] as const;

/** 周期类参数范围（后端 11.4 表：periods/fast/slow [2, 500]） */
const MIN_PERIOD = 2;
const MAX_PERIOD = 500;

/** 新条件的默认参数（保证首屏即合法） */
export const defaultCondition = (type: string): DcaCondition => {
    switch (type) {
        case 'EMA_CROSS':
            return {type, fast: 12, slow: 26, dir: 'UP'};
        case 'SMA_CROSS':
            return {type, fast: 5, slow: 20, dir: 'UP'};
        case 'PRICE_CROSS_EMA':
            return {type, period: 20, dir: 'UP'};
        case 'RSI':
            return {type, period: 14, mode: 'CROSS_UP', level: 30};
        case 'MACD_CROSS':
            return {type, dir: 'UP'};
        case 'PRICE_BELOW':
            return {type, value: 100};
        case 'DRAWDOWN':
            return {type, windowMin: 60, pct: 5};
        case 'LOW_TOUCH':
            return {type, periods: 10};
        default:
            return {type: 'EMA_CROSS', fast: 12, slow: 26, dir: 'UP'};
    }
};

type TFunc = (key: string) => string;

const inRange = (v: number | undefined, min: number, max: number): boolean =>
    null != v && v >= min && v <= max;

/**
 * 单条条件参数校验（与后端 DcaConditionValidator 11.4 表一致）
 * 返回错误文案；null=合法
 */
export const validateCondition = (cond: DcaCondition, t: TFunc): string | null => {
    switch (cond.type) {
        case 'EMA_CROSS':
        case 'SMA_CROSS':
            if (!inRange(cond.fast, MIN_PERIOD, MAX_PERIOD) || !inRange(cond.slow, MIN_PERIOD, MAX_PERIOD)) {
                return t('dca.cond.range').replace('{min}', String(MIN_PERIOD)).replace('{max}', String(MAX_PERIOD));
            }
            if ('UP' !== cond.dir && 'DOWN' !== cond.dir) {
                return t('dca.form.paramInvalid');
            }
            return null;
        case 'PRICE_CROSS_EMA':
            if (!inRange(cond.period, MIN_PERIOD, MAX_PERIOD)) {
                return t('dca.cond.range').replace('{min}', String(MIN_PERIOD)).replace('{max}', String(MAX_PERIOD));
            }
            if ('UP' !== cond.dir && 'DOWN' !== cond.dir) {
                return t('dca.form.paramInvalid');
            }
            return null;
        case 'RSI':
            if (!inRange(cond.period, MIN_PERIOD, MAX_PERIOD)) {
                return t('dca.cond.range').replace('{min}', String(MIN_PERIOD)).replace('{max}', String(MAX_PERIOD));
            }
            if (null == cond.level || cond.level <= 0 || cond.level >= 100) {
                return t('dca.cond.rsilevel');
            }
            if ('CROSS_UP' !== cond.mode && 'CROSS_DOWN' !== cond.mode) {
                return t('dca.form.paramInvalid');
            }
            return null;
        case 'MACD_CROSS':
            return 'UP' === cond.dir || 'DOWN' === cond.dir ? null : t('dca.form.paramInvalid');
        case 'PRICE_BELOW':
            return null != cond.value && cond.value > 0 ? null : t('dca.cond.valuePositive');
        case 'DRAWDOWN':
            if (null == cond.windowMin || cond.windowMin < 60) {
                return t('dca.cond.windowMinMin');
            }
            if (null == cond.pct || cond.pct <= 0 || cond.pct >= 100) {
                return t('dca.cond.pctRange');
            }
            return null;
        case 'LOW_TOUCH':
            return inRange(cond.periods, MIN_PERIOD, MAX_PERIOD)
                ? null
                : t('dca.cond.range').replace('{min}', String(MIN_PERIOD)).replace('{max}', String(MAX_PERIOD));
        default:
            return t('dca.form.paramInvalid');
    }
};

/** 占位符填充（null/undefined 显示为 --） */
const fill = (tpl: string, vars: Record<string, string | number | undefined>): string => {
    let out = tpl;
    Object.entries(vars).forEach(([k, v]) => {
        out = out.replace(`{${k}}`, null == v ? '--' : String(v));
    });
    return out;
};

/**
 * 方向文案（按条件类型区分 key；语义为位置关系「高于/低于」，非交叉事件——需求 §11.2）
 */
const dirText = (cond: DcaCondition, t: TFunc): string => {
    if ('UP' !== cond.dir && 'DOWN' !== cond.dir) {
        return '--';
    }
    const isUp = 'UP' === cond.dir;
    if ('EMA_CROSS' === cond.type || 'SMA_CROSS' === cond.type) {
        return isUp ? t('dca.dir.golden') : t('dca.dir.death');
    }
    return isUp ? t('dca.dir.crossUp') : t('dca.dir.crossDown');
};

/** 单条条件预览文案 */
export const buildConditionText = (cond: DcaCondition, t: TFunc): string => {
    switch (cond.type) {
        case 'EMA_CROSS':
            return fill(t('dca.preview.EMA_CROSS'), {dir: dirText(cond, t), fast: cond.fast, slow: cond.slow});
        case 'SMA_CROSS':
            return fill(t('dca.preview.SMA_CROSS'), {dir: dirText(cond, t), fast: cond.fast, slow: cond.slow});
        case 'PRICE_CROSS_EMA':
            return fill(t('dca.preview.PRICE_CROSS_EMA'), {dir: dirText(cond, t), period: cond.period});
        case 'RSI':
            return fill(t('dca.preview.RSI'), {
                period: cond.period,
                mode: 'CROSS_UP' === cond.mode ? t('dca.cond.mode.CROSS_UP') : t('dca.cond.mode.CROSS_DOWN'),
                level: cond.level,
            });
        case 'MACD_CROSS':
            return fill(t('dca.preview.MACD_CROSS'), {dir: dirText(cond, t)});
        case 'PRICE_BELOW':
            return fill(t('dca.preview.PRICE_BELOW'), {value: cond.value});
        case 'DRAWDOWN':
            return fill(t('dca.preview.DRAWDOWN'), {windowMin: cond.windowMin, pct: cond.pct});
        case 'LOW_TOUCH':
            return fill(t('dca.preview.LOW_TOUCH'), {periods: cond.periods});
        default:
            return '--';
    }
};

/** 组合预览文案（AND/OR 前缀 + 逐条） */
export const buildConditionsText = (conditions: DcaConditions | null, t: TFunc): string => {
    if (null == conditions || 0 === conditions.conditions.length) {
        return '';
    }
    const head = 'AND' === conditions.op ? t('dca.preview.and') : t('dca.preview.or');
    const lines = conditions.conditions.map((c) => buildConditionText(c, t));
    return `${head}\n${lines.map((l) => `· ${l}`).join('\n')}`;
};

/** 解析 conditionsJson（非法时返回 null） */
export const parseConditionsJson = (json?: string | null): DcaConditions | null => {
    if (null == json || '' === json) {
        return null;
    }
    try {
        const parsed = JSON.parse(json);
        if (null == parsed || !Array.isArray(parsed.conditions)) {
            return null;
        }
        return parsed as DcaConditions;
    } catch {
        return null;
    }
};

/**
 * 后端时间格式化（yyyy-MM-dd HH:mm:ss，兼容 ISO/毫秒时间戳）
 * 毫秒时间戳必须按数值解析：dayjs 对 13 位纯数字字符串会命中其 YYYYMMDD 正则
 * （年份取前4位、月份越界滚动），产出 1792/1793 等错误日期（测算结果曾现此缺陷）
 */
export const fmtTime = (value?: string | number | null): string => {
    if (null == value || '' === value) {
        return '--';
    }
    const raw = String(value).trim();
    const parsed = typeof value === 'number' || /^\d{13}$/.test(raw)
        ? dayjs(Number(value))
        : dayjs(raw.replace('T', ' ').replace(/\.\d+$/, '').slice(0, 19));
    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : '--';
};
